import { ArrowUp, ArrowDown, X } from "lucide-react";
import { TInput, Toggle, Btn, Card, Sel } from "./ui";
import { DEFAULT_NAV } from "../../context/SiteSettingsContext";

const ROUTE_OPTIONS = [
  { v: "/music", l: "MUSIC (/music)" },
  { v: "/#dimensions", l: "DIMENSIONS (home section)" },
  { v: "/visuals", l: "VISUALS (/visuals)" },
  { v: "/artist", l: "ABOUT / ARTIST (/artist)" },
  { v: "/store", l: "STORE (/store)" },
  { v: "/supporters", l: "SUPPORTERS (/supporters)" },
  { v: "/complete-set", l: "COMPLETE COLLECTION (/complete-set)" },
  { v: "/dimension-i", l: "DIMENSION I (/dimension-i)" },
  { v: "/join", l: "JOIN / MAILING LIST (/join)" },
  { v: "/inner-circle", l: "INNER CIRCLE (/inner-circle)" },
  { v: "/motion-network", l: "MOTION NETWORK (/motion-network)" },
  { v: "CUSTOM", l: "CUSTOM URL…" },
];

export default function NavigationCard({ s, set }) {
  const items = s.navigation?.length ? s.navigation : DEFAULT_NAV;
  const update = (list) => set("navigation", list);
  const setItem = (i, k, v) => update(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const copy = [...items];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    update(copy);
  };
  const remove = (i) => {
    if (!window.confirm(`Remove "${items[i].title}" from the menu? The page itself stays reachable — hiding it with the toggle is usually enough.`)) return;
    update(items.filter((_, idx) => idx !== i));
  };
  const add = () => update([...items, {
    id: `nav-${Date.now()}`, title: "NEW ITEM", route: "/music", enabled: false, newTab: false, customUrl: "",
  }]);

  return (
    <Card className="mt-6" data-testid="navigation-card">
      <p className="font-mono text-[10px] tracking-[0.3em] text-gold mb-1">NAVIGATION — PUBLIC MENU</p>
      <p className="font-mono text-[8px] tracking-[0.15em] text-white/30 mb-5 leading-relaxed">
        DESKTOP AND MOBILE MENUS BOTH USE THIS LIST. RENAMING A LABEL NEVER CHANGES THE UNDERLYING ROUTE. DISABLED ITEMS STAY REACHABLE BY DIRECT LINK. ACCOUNT, CART, CHECKOUT AND /ADMIN ARE SYSTEM ROUTES AND NEVER DEPEND ON THIS MENU.
      </p>
      {items.map((it, i) => {
        const isCustom = it.route === "CUSTOM" || (!!it.customUrl && !ROUTE_OPTIONS.some((o) => o.v === it.route));
        return (
          <div key={it.id || i} className={`border-t border-white/10 py-4 grid md:grid-cols-12 gap-3 items-end ${it.enabled === false ? "opacity-60" : ""}`} data-testid={`nav-item-${i}`}>
            <div className="md:col-span-3">
              <TInput label="MENU LABEL" value={it.title} onChange={(v) => setItem(i, "title", v)} testid={`nav-item-${i}-title`} />
            </div>
            <div className="md:col-span-3">
              <Sel label="DESTINATION" value={isCustom ? "CUSTOM" : it.route}
                onChange={(v) => setItem(i, "route", v)}
                options={ROUTE_OPTIONS} testid={`nav-item-${i}-route`} />
            </div>
            <div className="md:col-span-3">
              {isCustom ? (
                <TInput label="CUSTOM URL" value={it.customUrl} onChange={(v) => setItem(i, "customUrl", v)} placeholder="https://…" testid={`nav-item-${i}-custom-url`} />
              ) : (
                <p className="font-mono text-[8px] text-white/25 pb-3">ROUTE: {it.route}</p>
              )}
            </div>
            <div className="md:col-span-3 flex items-center justify-end gap-2 flex-wrap">
              <Toggle label="SHOWN" checked={it.enabled !== false} onChange={(v) => setItem(i, "enabled", v)} testid={`nav-item-${i}-enabled`} />
              <Toggle label="NEW TAB" checked={!!it.newTab} onChange={(v) => setItem(i, "newTab", v)} testid={`nav-item-${i}-newtab`} />
              <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" data-testid={`nav-item-${i}-up`}
                className="text-white/40 hover:text-white disabled:opacity-20 p-1.5 border border-white/10"><ArrowUp size={12} /></button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label="Move down" data-testid={`nav-item-${i}-down`}
                className="text-white/40 hover:text-white disabled:opacity-20 p-1.5 border border-white/10"><ArrowDown size={12} /></button>
              <button onClick={() => remove(i)} aria-label="Remove item" data-testid={`nav-item-${i}-remove`}
                className="text-white/40 hover:text-red-400 p-1.5 border border-white/10"><X size={12} /></button>
            </div>
          </div>
        );
      })}
      <div className="mt-4">
        <Btn variant="ghost" onClick={add} testid="nav-item-add">ADD MENU ITEM</Btn>
      </div>
    </Card>
  );
}
